import { influencer, outreach, TiktokAudio } from '@round/api';
import { buildTiktokHashtagUrl, buildTiktokMusicUrl, formatTo2dp, numberWithCommas } from 'helpers';

const RESOLVING_SOUND_LABEL = 'Loading sound';
const UNKNOWN_SOUND_LABEL = 'Unknown sound';

const RESOLVING_LOCATION_LABEL = 'Loading location';
const UNKNOWN_LOCATION_LABEL = 'Unknown location';

/** A filter with no bound is a decision the request was submitted with, not a gap to drop. */
const ANY = 'Any';

export type ConfigurationItemIcon = 'sound' | 'username' | 'link' | 'email' | 'location' | 'rate';

/** What the badge is, which is also how it is drawn. */
export type ConfigurationItemKind = 'target' | 'filter' | 'output';

export type ConfigurationItem = {
    /** Stable across the resolve, so a placeholder and the badge that replaces it are one element. */
    key: string;
    kind: ConfigurationItemKind;
    label: string;
    value?: string;
    href?: string;
    icon?: ConfigurationItemIcon;
    imageUrl?: string;
    countryCode?: string;
    isResolving?: boolean;
};

/** A sound is stored as an id, so naming it is a second read the caller does its own way. */
export type AudioLookup = {
    audiosById: Map<number, TiktokAudio>;
    isLoadingAudios: boolean;
};

/** Locations are stored as ids too, and named off the one country list every caller already holds. */
export type LocationLookup = {
    countriesById: Map<number, influencer.InfluencerLocation>;
    isLoadingCountries: boolean;
};

/** The two kinds of target as one list, for a reader who meets them under a single heading. */
export function getTargetItems(targets: outreach.OutreachTargets, lookup: AudioLookup): ConfigurationItem[] {
    return [...getSoundItems(targets.audios, lookup), ...getHashtagItems(targets.hashtags)];
}

export function getSoundItems(audioIds: number[], { audiosById, isLoadingAudios }: AudioLookup): ConfigurationItem[] {
    return audioIds.map((audioId) => {
        const key = `sound-${audioId}`;
        const audio = audiosById.get(audioId);

        if (!audio) {
            return {
                key,
                kind: 'target',
                label: isLoadingAudios ? RESOLVING_SOUND_LABEL : UNKNOWN_SOUND_LABEL,
                icon: 'sound',
                isResolving: isLoadingAudios,
            };
        }

        return {
            key,
            kind: 'target',
            label: audio.title,
            href: buildTiktokMusicUrl(audio.title, audio.tiktok_id),
            icon: 'sound',
            imageUrl: audio.cover_thumb,
        };
    });
}

export function getHashtagItems(hashtags: string[]): ConfigurationItem[] {
    return hashtags.map((hashtag) => ({
        key: `hashtag-${hashtag}`,
        kind: 'target',
        // Stored lowercased and without one, so the '#' is the page's to put back.
        label: `#${hashtag}`,
        href: buildTiktokHashtagUrl(hashtag),
    }));
}

/** The two that always come back lead, so only the extras a request asked for change the list. */
export function getOutputItems(outputs: outreach.OutreachOutputs): ConfigurationItem[] {
    return [
        { key: 'username', kind: 'output', label: 'Username', icon: 'username' },
        { key: 'link', kind: 'output', label: 'Link', icon: 'link' },
        ...getRequestedOutputItems(outputs),
    ];
}

/** The columns a request had to ask for, which are the only ones one request differs from another by. */
export function getRequestedOutputItems(outputs: outreach.OutreachOutputs): ConfigurationItem[] {
    const items: ConfigurationItem[] = [];

    if (outputs.email) {
        items.push({ key: 'email', kind: 'output', label: 'Email', icon: 'email' });
    }

    if (outputs.location) {
        items.push({ key: 'location', kind: 'output', label: 'Location', icon: 'location' });
    }

    if (outputs.rate) {
        items.push({ key: 'rate', kind: 'output', label: 'Rate', icon: 'rate' });
    }

    return items;
}

/** The places a request looks in, then the bounds it was narrowed by — each one carrying its value. */
export function getFilterItems(filters: outreach.OutreachFilters, lookup: LocationLookup): ConfigurationItem[] {
    return [...getLocationItems(filters.locations, lookup), ...getFilterValueItems(filters)];
}

function getLocationItems(
    locationIds: number[],
    { countriesById, isLoadingCountries }: LocationLookup
): ConfigurationItem[] {
    if (!locationIds.length) {
        return [{ key: 'locations', kind: 'filter', label: 'Locations', value: ANY }];
    }

    return locationIds.map((locationId) => {
        const country = countriesById.get(locationId);

        if (!country) {
            return {
                key: `location-${locationId}`,
                kind: 'filter',
                label: isLoadingCountries ? RESOLVING_LOCATION_LABEL : UNKNOWN_LOCATION_LABEL,
                isResolving: isLoadingCountries,
            };
        }

        return {
            key: `location-${locationId}`,
            kind: 'filter',
            label: country.country_name,
            countryCode: country.country_code,
        };
    });
}

function getFilterValueItems(filters: outreach.OutreachFilters): ConfigurationItem[] {
    return [
        { key: 'followers', kind: 'filter', label: 'Followers', value: getFollowersValue(filters) },
        {
            key: 'min-engagement-rate',
            kind: 'filter',
            label: 'Min engagement rate',
            value: getEngagementRateValue(filters),
        },
        {
            key: 'min-avg-views',
            kind: 'filter',
            label: 'Min average views',
            value: getCountValue(filters.min_avg_views),
        },
        {
            key: 'outside-locations',
            kind: 'filter',
            // 'loose' is the answer to "include creators outside the selected locations?".
            label: 'Outside locations',
            value: filters.location_match === 'loose' ? 'Included' : 'Excluded',
        },
    ];
}

function getFollowersValue({ min_followers, max_followers }: outreach.OutreachFilters): string {
    if (typeof min_followers === 'number' && typeof max_followers === 'number') {
        return `${numberWithCommas(min_followers)} — ${numberWithCommas(max_followers)}`;
    }

    if (typeof min_followers === 'number') {
        return `${numberWithCommas(min_followers)}+`;
    }

    if (typeof max_followers === 'number') {
        return `Up to ${numberWithCommas(max_followers)}`;
    }

    return ANY;
}

/** Stored as a fraction of 1, read by people as a percentage. */
function getEngagementRateValue({ min_engagement_rate }: outreach.OutreachFilters): string {
    if (typeof min_engagement_rate !== 'number') {
        return ANY;
    }

    return `${formatTo2dp(min_engagement_rate * 100)}%`;
}

function getCountValue(value: number | null): string {
    return typeof value === 'number' ? numberWithCommas(value) : ANY;
}
